import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Brain, 
  Settings, 
  BarChart3, 
  LogOut, 
  Play, 
  Clock, 
  CheckCircle,
  Zap,
  Users,
  Activity,
  Calculator,
  MessageCircle,
  Scan
} from 'lucide-react'

export default function Dashboard({ onLogout }) {
  const demos = [
    {
      id: 'rag-fiscal',
      title: 'RAG Fiscal Mexicano',
      description: 'Asistente inteligente especializado en legislación fiscal mexicana con respuestas precisas basadas en documentos oficiales.',
      status: 'active',
      icon: Brain,
      color: 'from-blue-500 to-blue-700',
      path: '/demo/rag',
      features: ['24 documentos legales', 'Respuestas en tiempo real', 'Referencias específicas'],
      stats: { queries: '1.2K', accuracy: '95%', uptime: '99.9%' }
    },
    {
      id: 'conciliacion',
      title: 'Conciliación Automática AI',
      description: 'Sistema inteligente que automatiza conciliaciones bancarias y contables con IA avanzada.',
      status: 'active',
      icon: Calculator,
      color: 'from-emerald-500 to-emerald-700',
      path: '/demo/conciliacion',
      features: ['Matching automático', 'Detección de discrepancias', 'Reportes inteligentes'],
      stats: { accuracy: '98.5%', time_saved: '85%', transactions: '10K+' }
    },
    {
      id: 'analytics',
      title: 'Analytics Dashboard',
      description: 'Dashboard avanzado de métricas y análisis de datos con visualizaciones interactivas.',
      status: 'active',
      icon: BarChart3,
      color: 'from-green-500 to-green-700',
      path: '/demo/analytics',
      features: ['Métricas en tiempo real', 'Visualizaciones', 'Reportes automáticos'],
      stats: { metrics: '100+', reports: '50+', insights: '24/7' }
    },
    {
      id: 'customer-service',
      title: 'Atención al Cliente IA',
      description: 'Sistema inteligente de atención multicanal con chatbot, análisis de sentimientos y escalamiento automático.',
      status: 'active',
      icon: MessageCircle,
      color: 'from-blue-600 to-cyan-600',
      path: '/demo/customer-service',
      features: ['Chatbot multicanal', 'Análisis sentimientos', 'Escalamiento inteligente'],
      stats: { resolution: '87%', time: '2.3min', satisfaction: '4.8/5' }
    },
    {
      id: 'document-automation',
      title: 'Automatización de Documentos',
      description: 'OCR inteligente y procesamiento automático de documentos con IA para extracción y validación de datos.',
      status: 'active',
      icon: Scan,
      color: 'from-orange-500 to-red-600',
      path: '/demo/document-automation',
      features: ['OCR inteligente', 'Extracción automática', 'Validación IA'],
      stats: { accuracy: '98.7%', processing: '12s', automation: '94.2%' }
    },
    {
      id: 'automation',
      title: 'Automatización N8N',
      description: 'Workflows automatizados para procesos empresariales con integración multi-canal.',
      status: 'coming-soon',
      icon: Settings,
      color: 'from-purple-500 to-purple-700',
      path: '/demo/automation',
      features: ['Workflows visuales', 'Integraciones API', 'Multi-canal'],
      stats: { workflows: '15+', integrations: '50+', efficiency: '80%' }
    }
    
  ]

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center mr-3">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">IQsit Demos</h1>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Bienvenido a la Plataforma de Demos
          </h2>
          <p className="text-lg text-gray-600">
            Explora las capacidades de nuestros servicios a través de demostraciones interactivas.
          </p>
        </motion.div>

        

        {/* Demos Grid */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          {demos.map((demo) => {
            const IconComponent = demo.icon
            
            return (
              <motion.div
                key={demo.id}
                variants={item}
                className="bg-white rounded-xl shadow-sm border hover:shadow-lg transition-all duration-300 overflow-hidden"
              >
                {/* Card Header */}
                <div className={`h-32 bg-gradient-to-br ${demo.color} relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black bg-opacity-10" />
                  <div className="relative h-full flex items-center justify-center">
                    <IconComponent className="w-12 h-12 text-white" />
                  </div>
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    {demo.status === 'active' ? (
                      <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                        Activo
                      </span>
                    ) : (
                      <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                        Próximamente
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {demo.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                    {demo.description}
                  </p>

                  {/* Features */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Características:</p>
                    <ul className="space-y-1">
                      {demo.features.map((feature, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                 

                  {/* Action Button */}
                  {demo.status === 'active' ? (
                    <Link
                      to={demo.path}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center transition-colors group"
                    >
                      <Play className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                      Probar Demo
                    </Link>
                  ) : (
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-500 py-3 px-4 rounded-lg font-medium flex items-center justify-center cursor-not-allowed"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Próximamente
                    </button>
                  )}
                </div>
              </motion.div>
            )
          })}
        </motion.div>
      </main>
    </div>
  )
} 